import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Sidebar from '../components/Sidebar';
import CustomerProfileSidebar from '../components/CustomerProfileSidebar';
import QuickReplyTemplates from '../components/QuickReplyTemplates';
import MessageStatusIcon from '../components/MessageStatusIcon';
import { useWebSocket } from '../hooks/useWebSocket';
import { useNotifications } from '../hooks/useNotifications';
import { useTranslation } from '../hooks/useTranslation';
import { useTrialStatus } from '@/context/TrialContext';
import { TrialExpiredInlineWarning } from '@/components/TrialExpiredBanner';
import { FaWhatsapp, FaInstagram, FaFacebookMessenger } from 'react-icons/fa';
import { 
  ArrowLeft, 
  Send, 
  Bot, 
  User, 
  Clock,
  CheckCircle,
  AlertCircle,
  RefreshCw,
  MoreVertical,
  UserCircle,
  Menu,
  Bell,
  BellOff,
  MessageCircle,
  Instagram,
  Globe,
  Camera,
  DollarSign,
  AlertTriangle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Card } from '../components/ui/card';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger,
  DropdownMenuSeparator
} from '../components/ui/dropdown-menu';
import { Lock } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const msgText = (m) => String(m?.content ?? m?.response ?? m?.text ?? '').trim();
const isOutgoingMsg = (m) =>
  m?.sender_type === 'human' || m?.sender_type === 'bot' || m?.role === 'assistant' || m?.type === 'outgoing';
const dedupeById = (list) => {
  const seen = new Set();
  return list.filter(m => (m?.id && seen.has(m.id) ? false : (seen.add(m?.id), true)));
};
const API = process.env.REACT_APP_BACKEND_URL;

const ConversationDetailPage = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const messagesEndRef = useRef(null);
  const { isTrialExpired, canPerformActions } = useTrialStatus();
  
  const [conversation, setConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [replyText, setReplyText] = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [profileSidebarOpen, setProfileSidebarOpen] = useState(false);
  const [templates, setTemplates] = useState([]);

  // WebSocket hook
  const { isConnected, lastMessage, subscribeToConversation, onMessage } = useWebSocket();
  
  // Notifications hook
  const { isSupported: notifSupported, permission, requestPermission, notifyNewMessage } = useNotifications();
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);

  // Sync notification state with browser permission
  useEffect(() => {
    if (permission === 'granted') {
      setNotificationsEnabled(true);
    } else {
      setNotificationsEnabled(false);
    }
  }, [permission]);

  // Fetch templates for shortcut detection
  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${BACKEND_URL}/api/templates`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.data.success) {
          setTemplates(response.data.templates || []);
        }
      } catch (error) {
        console.error('Failed to fetch templates:', error);
      }
    };
    fetchTemplates();
  }, []);

  // Handle input change with shortcut detection
  const handleInputChange = (e) => {
    const value = e.target.value;
    
    // Check if input matches a shortcut (e.g., /klasik)
    if (value.startsWith('/')) {
      const matchingTemplate = templates.find(t => 
        t.shortcut && t.shortcut.toLowerCase() === value.toLowerCase()
      );
      
      if (matchingTemplate) {
        // Replace shortcut with template content
        setReplyText(matchingTemplate.content);
        return;
      }
    }
    
    setReplyText(value);
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchConversation = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/conversations/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setConversation(response.data.conversation);
        const serverMessages = response.data.messages || [];
        setMessages(prev => {
          const serverIds = new Set(serverMessages.map(m => m.id));
          // Balasan yang sudah tersimpan di server dikenali dari isinya, supaya
          // gelembung optimistic tidak tertinggal sebagai kembaran
          const serverOutgoingText = new Set(
            serverMessages.filter(isOutgoingMsg).map(msgText).filter(Boolean)
          );
          const pendingOptimistic = prev.filter(m =>
            m._optimistic && !serverIds.has(m.id) && !serverOutgoingText.has(msgText(m))
          );
          return dedupeById([...serverMessages, ...pendingOptimistic]);
        });
      }
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch conversation:', error);
      setLoading(false);
    }
  };

  // Fetch conversation on mount
  useEffect(() => {
    fetchConversation();
  }, [id]);

  // Subscribe to WebSocket updates for this conversation
  useEffect(() => {
    if (isConnected && id) {
      subscribeToConversation(id);
    }
  }, [isConnected, id, subscribeToConversation]);

  // Handle WebSocket messages
  useEffect(() => {
    const unsubscribe = onMessage('conversation-detail', (data) => {
      if (data.type === 'new_message' && data.conversation_id === id) {
        // Add new message to list
        const newMsg = data.message;
        setMessages(prev => {
          // Avoid duplicates
          if (prev.some(m => m.id === newMsg.id)) return prev;
          // Skip outgoing human messages - already handled by optimistic update
          if (newMsg.sender_type === 'human' || newMsg.type === 'outgoing') return prev;
          return [...prev, newMsg];
        });
        
        // Show notification if it's from customer and window not focused
        if (!document.hasFocus() && newMsg.sender_type === 'customer') {
          notifyNewMessage(
            conversation?.customer_name,
            newMsg.query,
            id,
            navigate
          );
        }
      } else if (data.type === 'conversation_update' && data.conversation?.id === id) {
        setConversation(data.conversation);
      } else if (data.type === 'message_status' && data.conversation_id === id) {
        // Update message delivery status (sent, delivered, read)
        setMessages(prev => prev.map(msg => 
          (msg.wamid === data.message_wamid || msg.fb_message_id === data.message_id)
            ? { ...msg, delivery_status: data.status }
            : msg
        ));
      }
    });

    return () => unsubscribe();
  }, [id, onMessage, conversation?.customer_name, navigate, notifyNewMessage]);

  // Track if user is scrolling up
  const isUserScrolledUp = useRef(false);
  const prevMessageCount = useRef(0);

  // Scroll to bottom only for NEW messages (not on refetch)
  useEffect(() => {
    if (messages.length > prevMessageCount.current && !isUserScrolledUp.current) {
      scrollToBottom();
    }
    prevMessageCount.current = messages.length;
  }, [messages]);

  // Track user scroll position
  const handleChatScroll = (e) => {
    const el = e.target;
    const distFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    isUserScrolledUp.current = distFromBottom > 100;
  };

  // Poll for messages when WebSocket not connected (fallback)
  useEffect(() => {
    if (isConnected) return;
    const interval = setInterval(fetchConversation, 5000);
    return () => clearInterval(interval);
  }, [id, isConnected]);

  const handleSendReply = async (e) => {
    e.preventDefault();
    if (!replyText.trim()) return;

    const messageText = replyText.trim();
    const tempId = `temp-${Date.now()}`;
    
    // Optimistically add message to UI immediately
    const optimisticMessage = {
      id: tempId,
      response: messageText,
      text: messageText,
      content: messageText,
      sender_type: 'human',
      role: 'assistant',
      created_at: new Date().toISOString(),
      timestamp: new Date().toISOString(),
      delivery_status: 'sending',
      _optimistic: true
    };
    
    setMessages(prev => [...prev, optimisticMessage]);
    setReplyText(''); // Clear input immediately so user can type next message
    
    // Send to backend in background (non-blocking)
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${BACKEND_URL}/api/conversations/${id}/reply`,
        { message: messageText },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        // Ganti gelembung optimistic dengan data server — dan jangan sisakan
        // kembaran kalau polling sudah menarik pesan yang sama
        setMessages(prev => {
          const withoutTemp = prev.filter(m => m.id !== tempId);
          if (withoutTemp.some(m => m.id === response.data.message_id)) return withoutTemp;
          const confirmed = {
            ...optimisticMessage,
            id: response.data.message_id,
            delivery_status: response.data.delivery_status || 'sent',
            _optimistic: false,
          };
          return dedupeById([...withoutTemp, confirmed]);
        });
      } else {
        // Server menolak: tandai gagal supaya bisa dicoba lagi, bukan menggantung
        setMessages(prev => prev.map(msg =>
          msg.id === tempId ? { ...msg, delivery_status: 'failed' } : msg
        ));
      }
    } catch (error) {
      console.error('Failed to send reply:', error);
      // Mark message as failed
      setMessages(prev => prev.map(msg => 
        msg.id === tempId 
          ? { ...msg, delivery_status: 'failed' }
          : msg
      ));
    }
  };

  // Retry failed message
  const handleRetryMessage = async (failedMsg) => {
    // Update status to sending
    setMessages(prev => prev.map(msg => 
      msg.id === failedMsg.id 
        ? { ...msg, delivery_status: 'sending' }
        : msg
    ));
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${BACKEND_URL}/api/conversations/${id}/reply`,
        { message: failedMsg.text || failedMsg.response || failedMsg.content },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        setMessages(prev => dedupeById(prev.map(msg =>
          msg.id === failedMsg.id
            ? {
                ...msg,
                id: response.data.message_id,
                delivery_status: response.data.delivery_status || 'sent',
                _optimistic: false,
              }
            : msg
        )));
      }
    } catch (error) {
      console.error('Retry failed:', error);
      setMessages(prev => prev.map(msg => 
        msg.id === failedMsg.id 
          ? { ...msg, delivery_status: 'failed' }
          : msg
      ));
    }
  };

  const handleHandoff = async (action) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${BACKEND_URL}/api/conversations/${id}/handoff`,
        { action },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchConversation();
    } catch (error) {
      console.error('Failed to change handoff:', error);
    }
  };

  const handleStatusChange = async (status) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${BACKEND_URL}/api/conversations/${id}/status?status=${status}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchConversation();
    } catch (error) {
      console.error('Failed to change status:', error);
    }
  };

  const handleEnableNotifications = async () => {
    const granted = await requestPermission();
    setNotificationsEnabled(granted);
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    
    let date;
    
    // Handle various timestamp formats
    if (typeof timestamp === 'number') {
      // Unix timestamp (seconds or milliseconds)
      date = new Date(timestamp < 10000000000 ? timestamp * 1000 : timestamp);
    } else if (typeof timestamp === 'string') {
      date = new Date(timestamp);
    } else if (timestamp instanceof Date) {
      date = timestamp;
    } else if (timestamp.$date) {
      // MongoDB date format
      date = new Date(timestamp.$date);
    } else {
      date = new Date(timestamp);
    }
    
    // Check if date is valid
    if (isNaN(date.getTime())) {
      return '';
    }
    
    // Force WIB timezone (UTC+7)
    return date.toLocaleTimeString('id-ID', { 
      hour: '2-digit', 
      minute: '2-digit',
      timeZone: 'Asia/Jakarta'
    });
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    
    let date;
    
    // Handle various timestamp formats
    if (typeof timestamp === 'number') {
      date = new Date(timestamp < 10000000000 ? timestamp * 1000 : timestamp);
    } else if (typeof timestamp === 'string') {
      date = new Date(timestamp);
    } else if (timestamp instanceof Date) {
      date = timestamp;
    } else if (timestamp.$date) {
      date = new Date(timestamp.$date);
    } else {
      date = new Date(timestamp);
    }
    
    if (isNaN(date.getTime())) {
      return '';
    }
    
    // Force WIB timezone (UTC+7)
    return date.toLocaleDateString('id-ID', { 
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      timeZone: 'Asia/Jakarta'
    });
  };

  const getChannelIcon = (channel) => {
    switch(channel) {
      case 'whatsapp': return <FaWhatsapp className="w-4 h-4 text-green-500" />;
      case 'instagram': return <FaInstagram className="w-4 h-4 text-pink-500" />;
      case 'messenger': return <FaFacebookMessenger className="w-4 h-4 text-blue-500" />;
      case 'tiktok': return <span className="w-4 h-4 text-black dark:text-zinc-50">T</span>;
      case 'messenger': return <FaFacebookMessenger className="w-4 h-4 text-blue-500" />;
      default: return <Globe className="w-4 h-4 text-slate-500 dark:text-zinc-400" />;
    }
  };

  const getChannelName = (channel) => {
    switch(channel) {
      case 'whatsapp': return 'WhatsApp';
      case 'instagram': return 'Instagram';
      case 'messenger': return 'Messenger';
      default: return 'Website';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-[#0A0A0A] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (!conversation) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-[#0A0A0A]">
        <div className="flex">
          <Sidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />
          <div className="flex-1 lg:ml-64 p-6">
            <Card className="p-12 text-center">
              <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-slate-900 dark:text-zinc-50 mb-2">
                {t('conversation.notFound') || 'Conversation not found'}
              </h3>
              <Button onClick={() => navigate('/inbox')}>
                {t('conversation.backToInbox')}
              </Button>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-[#0A0A0A]">
      <div className="flex">
        <Sidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />
        
        <div className="flex-1 lg:ml-64 flex flex-col h-screen">
          {/* Header - Fixed position */}
          <div className="fixed top-0 left-0 right-0 lg:left-64 bg-white dark:bg-zinc-900 border-b border-slate-200 dark:border-zinc-800 p-3 sm:p-4 z-30">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 sm:gap-4 min-w-0">
                <Button 
                  variant="ghost" 
                  size="icon" 
                  onClick={() => navigate('/inbox')}
                  className="flex-shrink-0"
                >
                  <ArrowLeft className="w-5 h-5" />
                </Button>
                
                <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                  {/* Avatar with Channel Icon */}
                  <div className="relative flex-shrink-0">
                    {(conversation.customer_avatar || conversation.customer_profile_pic) ? (
                      <img 
                        src={conversation.customer_avatar || conversation.customer_profile_pic} 
                        alt={conversation.customer_name || 'Customer'}
                        className="w-10 h-10 sm:w-12 sm:h-12 rounded-full object-cover"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    <div 
                      className={`w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-gradient-to-br from-slate-200 to-slate-300 dark:from-slate-600 dark:to-slate-700 flex items-center justify-center text-slate-600 dark:text-zinc-300 text-lg sm:text-xl font-bold ${(conversation.customer_avatar || conversation.customer_profile_pic) ? 'hidden' : ''}`}
                    >
                      {/* For Instagram, use first letter after @ */}
                      {conversation.channel === 'instagram' && conversation.customer_name?.startsWith('@')
                        ? conversation.customer_name.charAt(1)?.toUpperCase() || '?'
                        : conversation.customer_name?.charAt(0)?.toUpperCase() || '?'
                      }
                    </div>
                    {/* Channel badge */}
                    <div className={`absolute -bottom-0.5 -right-0.5 w-5 h-5 rounded-full flex items-center justify-center ring-2 ring-white dark:ring-slate-800 ${
                      conversation.channel === 'whatsapp' ? 'bg-green-500' :
                      conversation.channel === 'instagram' ? 'bg-gradient-to-br from-purple-500 via-pink-500 to-orange-400' :
                      conversation.channel === 'messenger' ? 'bg-indigo-500' : 
                      conversation.channel === 'tiktok' ? 'bg-black' : 'bg-slate-500'
                    }`}>
                      {conversation.channel === 'whatsapp' && <FaWhatsapp className="w-3 h-3 text-white" />}
                      {conversation.channel === 'instagram' && <FaInstagram className="w-3 h-3 text-white" />}
                      {conversation.channel === 'messenger' && <FaFacebookMessenger className="w-3 h-3 text-white" />}
                      {conversation.channel === 'tiktok' && <span className="text-white text-xs font-bold">T</span>}
                      {!['whatsapp', 'instagram', 'messenger'].includes(conversation.channel) && <Globe className="w-3 h-3 text-white" />}
                    </div>
                  </div>
                  <div className="min-w-0">
                    <h2 className="font-semibold text-slate-900 dark:text-zinc-50 flex items-center gap-2 truncate text-sm sm:text-base">
                      <span className="truncate">{conversation.customer_name || 'Customer'}</span>
                      <Badge variant="outline" className="text-xs hidden sm:inline-flex flex-shrink-0 gap-1">
                        {getChannelIcon(conversation.channel)} {getChannelName(conversation.channel)}
                      </Badge>
                    </h2>
                    {conversation.customer_phone && (
                      <p className="text-xs sm:text-sm text-slate-500 dark:text-zinc-400 truncate">
                        {conversation.customer_phone}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-1 sm:gap-3 flex-shrink-0">
                {/* Notification Status Indicator */}
                {notifSupported && (
                  <div className="hidden sm:flex items-center gap-1 text-xs">
                    {notificationsEnabled ? (
                      <><Bell className="w-3 h-3 text-blue-500" /><span className="text-indigo-600 dark:text-indigo-400">Notif ON</span></>
                    ) : (
                      <><BellOff className="w-3 h-3 text-slate-500 dark:text-zinc-400" /><span className="text-slate-500 dark:text-zinc-400">Notif OFF</span></>
                    )}
                  </div>
                )}

                {/* Status Badge - Hidden on mobile */}
                <Badge 
                  className="hidden sm:inline-flex"
                  variant={
                    conversation.status === 'resolved' ? 'default' :
                    conversation.status === 'urgent' ? 'destructive' :
                    conversation.status === 'attention' ? 'warning' : 'secondary'
                  }
                >
                  {conversation.status === 'resolved' && <CheckCircle className="w-3 h-3 mr-1" />}
                  {conversation.status === 'urgent' && <AlertCircle className="w-3 h-3 mr-1" />}
                  {conversation.status === 'resolved' ? 'Selesai' : 
                   conversation.status === 'open' ? 'Aktif' : conversation.status}
                </Badge>

                {/* Handler Badge - Shows Bot or Manual */}
                <Badge 
                  className="hidden md:inline-flex" 
                  variant={conversation.handled_by === 'bot' ? 'secondary' : 'default'}
                >
                  {conversation.handled_by === 'bot' ? (
                    <><Bot className="w-3 h-3 mr-1" /> Bot</>
                  ) : (
                    <><User className="w-3 h-3 mr-1" /> Manual</>
                  )}
                </Badge>

                {/* Customer Profile Button */}
                <Button 
                  variant="outline" 
                  size="icon"
                  onClick={() => setProfileSidebarOpen(!profileSidebarOpen)}
                  className="hidden lg:inline-flex"
                >
                  <UserCircle className="w-4 h-4" />
                </Button>

                {/* Actions Menu */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="icon">
                      <MoreVertical className="w-4 h-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    {/* Mobile-only options */}
                    <div className="sm:hidden">
                      <DropdownMenuItem className="text-xs text-slate-500 dark:text-zinc-400">
                        {t('conversation.status')}: {conversation.status === 'resolved' ? t('inbox.resolved') : 
                                conversation.status === 'open' ? t('inbox.active') : conversation.status}
                      </DropdownMenuItem>
                      <DropdownMenuItem className="text-xs text-slate-500 dark:text-zinc-400">
                        {t('conversation.handledBy')}: {conversation.handled_by === 'bot' ? t('inbox.bot') : t('inbox.human')}
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                    </div>
                    
                    {/* Profile sidebar on mobile */}
                    <DropdownMenuItem 
                      onClick={() => setProfileSidebarOpen(true)}
                      className="lg:hidden"
                    >
                      <UserCircle className="w-4 h-4 mr-2" />
                      {t('conversation.viewCustomerProfile') || 'View Customer Profile'}
                    </DropdownMenuItem>
                    
                    <DropdownMenuSeparator className="lg:hidden" />

                    {/* Notification Toggle */}
                    {notifSupported && (
                      <>
                        <DropdownMenuItem onClick={handleEnableNotifications}>
                          {notificationsEnabled ? (
                            <><Bell className="w-4 h-4 mr-2 text-indigo-600 dark:text-indigo-400" /> {t('inbox.notificationsActive')} <CheckCircle className="w-3 h-3 inline ml-1 text-green-500" /></>
                          ) : (
                            <><BellOff className="w-4 h-4 mr-2" /> {t('inbox.enableNotifications')}</>
                          )}
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                      </>
                    )}

                    {/* Handler Actions */}
                    {conversation.handled_by === 'bot' ? (
                      <DropdownMenuItem onClick={() => handleHandoff('takeover')}>
                        <User className="w-4 h-4 mr-2" />
                        {t('conversation.takeOver')}
                      </DropdownMenuItem>
                    ) : (
                      <DropdownMenuItem onClick={() => handleHandoff('return_to_bot')}>
                        <Bot className="w-4 h-4 mr-2" />
                        {t('conversation.handToBot')}
                      </DropdownMenuItem>
                    )}
                    
                    <DropdownMenuSeparator />
                    
                    {/* Status Actions */}
                    {conversation.status !== 'resolved' ? (
                      <DropdownMenuItem onClick={() => handleStatusChange('resolved')}>
                        <CheckCircle className="w-4 h-4 mr-2 text-green-600 dark:text-green-400" />
                        {t('conversation.markResolved')}
                      </DropdownMenuItem>
                    ) : (
                      <DropdownMenuItem onClick={() => handleStatusChange('open')}>
                        <RefreshCw className="w-4 h-4 mr-2" />
                        {t('conversation.reopen')}
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          </div>

          {/* Main Content Area with Optional Profile Sidebar */}
          <div className="flex-1 flex overflow-hidden pt-[60px] sm:pt-[72px]">
            {/* Messages Area */}
            <div className="flex-1 flex flex-col overflow-hidden">
              <div className="flex-1 overflow-y-auto p-3 sm:p-4 space-y-3 sm:space-y-4 bg-slate-100 dark:bg-[#0A0A0A]" onScroll={handleChatScroll}>
                {messages.length === 0 ? (
                  <div className="text-center py-12">
                    <p className="text-slate-500 dark:text-zinc-400">
                      {t('conversation.noMessages')}
                    </p>
                  </div>
                ) : (
                  messages.map((msg, index) => {
                    const isCustomer = msg.sender_type === 'customer' || msg.role === 'customer' || msg.query;
                    const prevMsg = messages[index - 1];
                    const showDate = !prevMsg || 
                      formatDate(msg.created_at || msg.timestamp) !== formatDate(prevMsg.created_at || prevMsg.timestamp);
                    
                    return (
                      <React.Fragment key={msg.id || index}>
                        {showDate && (formatDate(msg.created_at || msg.timestamp)) && (
                          <div className="text-center my-4">
                            <span className="px-3 py-1 bg-slate-200 dark:bg-zinc-800 rounded-full text-xs text-slate-600 dark:text-zinc-300">
                              {formatDate(msg.created_at || msg.timestamp)}
                            </span>
                          </div>
                        )}
                        
                        <div className={`flex ${isCustomer ? 'justify-start' : 'justify-end'}`}>
                          <div className="max-w-[85%] sm:max-w-[70%]">
                            {/* Message Bubble */}
                            <div
                              className={`rounded-xl px-3 py-2 sm:px-4 sm:py-2 ${
                                isCustomer
                                  ? 'bg-white dark:bg-zinc-900 text-slate-900 dark:text-zinc-50 rounded-bl-sm'
                                  : msg.sender_type === 'human' 
                                    ? 'bg-indigo-500 text-white rounded-br-sm'
                                    : 'bg-indigo-500 text-white rounded-br-sm'
                              }`}
                            >
                              {/* Sender indicator for non-customer messages */}
                              {!isCustomer && (
                                <div className="text-xs opacity-75 mb-1 flex items-center gap-1">
                                  {msg.sender_type === 'human' ? (
                                    <><User className="w-3 h-3" /> Anda</>
                                  ) : (
                                    <><Bot className="w-3 h-3" /> Bot</>
                                  )}
                                </div>
                              )}
                              
                              {/* Render image message (for bot/WhatsApp image messages) */}
                              {msg.message_type === 'image' && msg.image_url && (
                                <div className="mb-2">
                                  <div className="rounded-lg overflow-hidden bg-white/10 max-w-xs dark:bg-zinc-900">
                                    <img 
                                      src={`${API}/api/image-proxy?url=${encodeURIComponent(msg.image_url)}`}
                                      alt={msg.product_name || 'Product'} 
                                      className="w-full max-h-48 object-contain bg-slate-100 dark:bg-zinc-800"
                                      loading="eager"
                                      onError={(e) => { 
                                        e.target.onerror = null;
                                        e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100"><rect fill="%23ddd" width="100" height="100"/><text x="50%" y="50%" font-size="12" text-anchor="middle" dy=".3em" fill="%23999">No Image</text></svg>';
                                      }}
                                    />
                                    {msg.product_name && (
                                      <div className="px-3 py-2 bg-black/20">
                                        <div className="font-medium text-sm"><Camera className="w-3 h-3 inline mr-1" />{msg.product_name}</div>
                                        {msg.product_price && (
                                          <div className="text-xs opacity-90"><DollarSign className="w-3 h-3 inline mr-1" />{msg.product_price}</div>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )}
                              
                              {/* Also render image if image_url exists but message_type is not explicitly 'image' */}
                              {msg.image_url && msg.message_type !== 'image' && (
                                <div className="mb-2">
                                  <div className="rounded-lg overflow-hidden bg-white/10 max-w-xs dark:bg-zinc-900">
                                    <img 
                                      src={`${API}/api/image-proxy?url=${encodeURIComponent(msg.image_url)}`}
                                      alt="Product" 
                                      className="w-full max-h-48 object-contain bg-slate-100 dark:bg-zinc-800"
                                      loading="eager"
                                      onError={(e) => { 
                                        e.target.onerror = null;
                                        e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100"><rect fill="%23ddd" width="100" height="100"/><text x="50%" y="50%" font-size="12" text-anchor="middle" dy=".3em" fill="%23999">No Image</text></svg>';
                                      }}
                                    />
                                  </div>
                                </div>
                              )}
                              
                              {/* Render text content (show for all messages, including image messages with caption) */}
                              {(msg.text || msg.content || (isCustomer ? msg.query : msg.response)) && (
                                <p className="whitespace-pre-wrap break-words text-sm sm:text-base">
                                  {msg.text || msg.content || (isCustomer ? msg.query : msg.response) || ''}
                                </p>
                              )}
                              
                              {/* Render image attachments */}
                              {msg.attachments && msg.attachments.length > 0 && (
                                <div className="mt-2 space-y-2">
                                  {msg.attachments.filter(att => att.type === 'image').map((att, attIndex) => (
                                    <div key={attIndex} className="rounded-lg overflow-hidden bg-white/10 dark:bg-zinc-900">
                                      <img 
                                        src={att.url} 
                                        alt={att.product_name || 'Product'} 
                                        className="w-full max-h-48 object-contain bg-slate-100 dark:bg-zinc-800"
                                        loading="eager"
                                        crossOrigin="anonymous"
                                        referrerPolicy="no-referrer"
                                        onError={(e) => { 
                                          e.target.onerror = null;
                                          e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100"><rect fill="%23ddd" width="100" height="100"/><text x="50%" y="50%" font-size="12" text-anchor="middle" dy=".3em" fill="%23999">No Image</text></svg>';
                                        }}
                                      />
                                      {att.product_name && (
                                        <div className="px-2 py-1 text-xs opacity-90">
                                          <Camera className="w-3 h-3 inline mr-1" />{att.product_name}
                                        </div>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                            
                            {/* Timestamp and Delivery Status */}
                            <div className={`flex items-center gap-1 text-xs text-slate-500 mt-1 ${isCustomer ? 'justify-start' : 'justify-end'} dark:text-zinc-400`}>
                              <span>{formatTime(msg.created_at || msg.timestamp)}</span>
                              {/* Show delivery status for outgoing messages */}
                              {!isCustomer && (
                                <>
                                  <MessageStatusIcon status={msg.delivery_status} className="w-3.5 h-3.5" />
                                  {msg.delivery_status === 'failed' && (
                                    <>
                                      <span className="text-red-500 dark:text-red-400" data-testid="msg-failed-label">
                                        Gagal terkirim{msg.delivery_error ? ` — ${String(msg.delivery_error).slice(0, 80)}` : ''}
                                      </span>
                                      <button 
                                        onClick={() => handleRetryMessage(msg)}
                                        className="ml-1 text-red-500 hover:text-red-600 dark:hover:text-red-400 underline text-xs dark:text-red-400"
                                      >
                                        Retry
                                      </button>
                                    </>
                                  )}
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      </React.Fragment>
                    );
                  })
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Reply Input */}
              <div className="bg-white dark:bg-zinc-900 border-t border-slate-200 dark:border-zinc-800 p-3 sm:p-4">
                {/* Trial Expired Warning */}
                {isTrialExpired && (
                  <div className="mb-3">
                    <TrialExpiredInlineWarning />
                  </div>
                )}
                
                <form onSubmit={handleSendReply} className="flex gap-2 sm:gap-3">
                  {/* Quick Reply Templates Button */}
                  <QuickReplyTemplates 
                    onSelectTemplate={(content) => !isTrialExpired && setReplyText(content)}
                    compact={true}
                    disabled={isTrialExpired}
                  />
                  
                  <Input
                    value={replyText}
                    onChange={handleInputChange}
                    placeholder={isTrialExpired ? "Upgrade untuk mengirim pesan" : t('conversation.typeMessage')}
                    className={`flex-1 text-sm sm:text-base ${isTrialExpired ? 'opacity-50 cursor-not-allowed' : ''}`}
                    disabled={isTrialExpired}
                  />
                  <Button 
                    type="submit" 
                    disabled={!replyText.trim() || isTrialExpired} 
                    size="default" 
                    className={`${isTrialExpired ? 'bg-slate-400' : 'bg-indigo-600 hover:bg-indigo-700'} text-white`}
                  >
                    {isTrialExpired ? <Lock className="w-4 h-4" /> : <Send className="w-4 h-4" />}
                    <span className="ml-2 hidden sm:inline">{isTrialExpired ? 'Terkunci' : t('conversation.send')}</span>
                  </Button>
                </form>
                
                {/* Quick Info */}
                <div className="mt-2 text-xs text-slate-500 dark:text-zinc-400 flex flex-wrap items-center gap-2 sm:gap-4">
                  <span className="flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    via {conversation.channel}
                  </span>
                  {conversation.handled_by === 'bot' && (
                    <span className="text-yellow-600 dark:text-yellow-400">
                      <AlertTriangle className="w-3 h-3 inline mr-1" />{t('conversation.botActive')}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Customer Profile Sidebar - Desktop */}
            <CustomerProfileSidebar
              conversation={conversation}
              messages={messages}
              isOpen={profileSidebarOpen}
              onClose={() => setProfileSidebarOpen(false)}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConversationDetailPage;
